import { useState, useEffect } from 'react';
import { ArrowLeft, FileText, MessageSquare, Ticket, Hotel, Users, MapPin, Calendar, Car, Download, ChevronDown, ChevronUp, X, ImageUp, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { User } from '../App';
import { TourFileCover } from './TourFileCover';
import { SendToHotelModal } from './SendToHotelModal';
import { tourFileAPI, seriesTemplateAPI } from '../services/api';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

declare const process: {
  env: {
    REACT_APP_API_URL?: string;
  };
};

interface TourFileDetailProps {
  tourId: string | null;
  onBack: () => void;
  currentUser: User;
}

// Code lookup tables for file code generation
const CITY_CODES: {[key: string]: string} = {
  'Delhi': 'DL',
  'Bhunbaneswar': 'BB',
  'Cochin': 'CK'
};

const FILE_MANAGER_CODES: {[key: string]: string} = {
  'Anima Biswal': 'AB',
  'Madhu Chaudhary': 'MC',
  'Sumesh Sudharasan': 'SS',
  'Raghwendra Kumar': 'RK',
  'Pamlesh Rana': 'PR',
  'Ujjwal': 'UK',
  'Shambu Rawat': 'SR',
  'Jitendra Yadav': 'JY',
  'Abhay Raj Singh': 'AS',
  'NIdhi Gopal': 'NG',
  'Ketan Gupta': 'KG',
  'Sunil Verma': 'SV'
};

const AGENT_CODES: {[key: string]: string} = {
  'Venus Travel': 'VT',
  'Viva Holidays': 'VH',
  'Inspiring Vacations': 'IV',
  'Sanskriti Tours': 'ST',
  'Soulful Safaris': 'SS',
  'Direct Client': 'DC',
  'Referral Client': 'RF',
  '24x7 Rooms com': 'RC',
  'Trip Tailor': 'TT',
  'Muziris Heritage': 'MH',
  'Explore': 'EX',
  'Fest Travel': 'FT',
  'Japan': 'JP',
  'ROCS': 'RO',
  'Buckenham Travel': 'BT',
  'Celebration India Holidays': 'CI',
  'Hooting Owls': 'HO',
  'Royal Mountain Tours': 'RM',
  'Flywell Tours': 'FW',
  'Destinations World wide': 'DW',
  'Business Promotion / FAM': 'BP'
};

// Operator to Country mapping for auto-fill
const OPERATOR_COUNTRIES: {[key: string]: string} = {
  'Viva Holidays': 'UK',
  'Inspiring Vacations': 'Australia',
  'Sanskriti Tours': 'Netherlands',
  'Soulful Safaris': 'USA',
  '24x7 Rooms com': 'Bahrain',
  'Muziris Heritage': 'Kerala',
  'Explore': 'UK',
  'Fest Travel': 'Turkey',
  'Japan': 'Japan',
  'Buckenham Travel': 'Goa',
  'Royal Mountain Tours': 'Nepal',
  'Flywell Tours': 'Kerala'
};

// Helper function to extract base pattern from file code (excluding month/year/sequence)
function getFileCodeBasePattern(fileCode: string): string | null {
  if (!fileCode || fileCode.length < 10) return null;
  try {
    const match = fileCode.match(/^([A-Z]{2})([A-Z]{2})\d{2}\d{2}([A-Z]{2})\d{4}$/);
    if (match) {
      return match[1] + match[2] + match[3]; // City + Manager + Operator
    }
    return null;
  } catch (e) {
    return null;
  }
}

// Helper function to parse dd-mmm-yy format date to YYYY-MM-DD
const parseDateFormat = (dateStr: string): string => {
  const parts = dateStr.trim().split('-');
  if (parts.length !== 3) return '';
  
  const day = parts[0];
  const monthStr = parts[1];
  const year = '20' + parts[2]; // Convert 26 to 2026
  
  const monthMap: {[key: string]: string} = {
    'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
    'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
    'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
  };
  
  const month = monthMap[monthStr];
  if (!month) return '';
  
  return `${year}-${month}-${day.padStart(2, '0')}`;
};

export function TourFileDetail({ tourId, onBack, currentUser }: TourFileDetailProps) {
  const [tour, setTour] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isNewTour, setIsNewTour] = useState(false);
  const [tourType, setTourType] = useState<'TAILOR_MADE' | 'SERIES'>('TAILOR_MADE');
  const [seriesMode, setSeriesMode] = useState<'single' | 'multiple' | null>(null); // null = not selected yet
  const [arrivalDates, setArrivalDates] = useState<string>(''); // Comma-separated dates in dd-mmm-yy format
  const [selectedDates, setSelectedDates] = useState<Date[]>([]); // Selected dates for calendar
  const [showCalendar, setShowCalendar] = useState(false); // Toggle calendar visibility
  const [isEditingCover, setIsEditingCover] = useState(false); // Track TourFileCover edit state
  const [seriesFields, setSeriesFields] = useState({
    fileName: '',
    country: '',
    city: 'Delhi',
    fileManager: currentUser.name,
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    fileTourOperator: ''
  });
  const [seriesTemplates, setSeriesTemplates] = useState<any[]>([]);
  const [showExtensionForm, setShowExtensionForm] = useState(false);
  const [extensionPassengerName, setExtensionPassengerName] = useState('');
  const [extensions, setExtensions] = useState<Array<{
    id: string;
    passengerName: string;
    position: 'pre' | 'post';
    date: string;
    sector: string;
    flightNumber: string;
    time: string;
    status: string;
    guideName: string;
    guideType: string;
    city: string;
    hotelName: string;
    hotelStatus: string;
    roomCategory: string;
    meals: string[];
    breakfastRemark: string;
    lunchRemark: string;
    dinnerRemark: string;
  }>>([]);
  const [currentExtension, setCurrentExtension] = useState({
    id: '',
    passengerName: '',
    position: 'post' as 'pre' | 'post',
    date: '',
    sector: '',
    flightNumber: '',
    time: '',
    status: '',
    guideName: '',
    guideType: '',
    city: '',
    hotelName: '',
    hotelStatus: '',
    roomCategory: '',
    meals: ['Breakfast'],
    breakfastRemark: '',
    lunchRemark: '',
    dinnerRemark: ''
  });
  // State for managing multiple rows within a single extension entry
  const [extensionRows, setExtensionRows] = useState<Array<{
    id: string;
    passengerName: string;
    position: 'pre' | 'post';
    date: string;
    sector: string;
    flightNumber: string;
    time: string;
    status: string;
    guideName: string;
    guideType: string;
    city: string;
    hotelName: string;
    hotelStatus: string;
    roomCategory: string;
    meals: string[];
    breakfastRemark: string;
    lunchRemark: string;
    dinnerRemark: string;
  }>>([{
    id: '1',
    passengerName: '',
    position: 'post',
    date: '',
    sector: '',
    flightNumber: '',
    time: '',
    status: '',
    guideName: '',
    guideType: '',
    city: '',
    hotelName: '',
    hotelStatus: '',
    roomCategory: '',
    meals: ['Breakfast'],
    breakfastRemark: '',
    lunchRemark: '',
    dinnerRemark: ''
  }]);
  
  // State for passenger details
  const [passengerDetails, setPassengerDetails] = useState<Array<{
    id: string;
    roomType: string;
    roomNumber?: string;
    name: string;
    dob: string;
    age: string;
    sex: string;
    nationality: string;
    passportNo: string;
    issueDate: string;
    expiryDate: string;
    placeOfIssue: string;
    dietaryRequirements: string;
  }>>([]);
  const [passengerAttachments, setPassengerAttachments] = useState<Array<{
    filename: string;
    mimeType: string;
    contentBase64: string;
  }>>([]);
  const [extensionPosition, setExtensionPosition] = useState<'pre' | 'post'>('post');
  const [showBookingMailModal, setShowBookingMailModal] = useState(false);
  const [selectedHotelForMail, setSelectedHotelForMail] = useState('');
  const [selectedHotelBookings, setSelectedHotelBookings] = useState<any[]>([]);

  const normalizeDate = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return '';

    const directMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (directMatch) return trimmed;

    const slashMatch = trimmed.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/);
    if (slashMatch) {
      const day = slashMatch[1].padStart(2, '0');
      const month = slashMatch[2].padStart(2, '0');
      const rawYear = slashMatch[3];
      const year = rawYear.length === 2 ? `20${rawYear}` : rawYear;
      return `${year}-${month}-${day}`;
    }

    return '';
  };

  const extractPassengerFromText = (text: string) => {
    const compact = text.replace(/\r/g, '\n');

    const get = (regexes: RegExp[]) => {
      for (const regex of regexes) {
        const match = compact.match(regex);
        if (match && match[1]) return match[1].trim();
      }
      return '';
    };

    const name = get([
      /(?:Passenger\s*Name|Name)\s*[:\-]\s*([^\n]+)/i,
      /(?:Mr|Mrs|Ms|Miss)\.?\s+([A-Za-z\s]{4,})/i
    ]);

    const passportNo = get([
      /(?:Passport\s*(?:No|Number)?)\s*[:\-]\s*([A-Z0-9]+)/i
    ]);

    const nationality = get([
      /(?:Nationality)\s*[:\-]\s*([A-Za-z\s]+)/i
    ]);

    const sexRaw = get([
      /(?:Sex|Gender)\s*[:\-]\s*(Male|Female|M|F)/i,
      /\b(Male|Female)\b/i
    ]);

    const dobRaw = get([
      /(?:DOB|Date\s*of\s*Birth)\s*[:\-]\s*([^\n]+)/i
    ]);

    const issueDateRaw = get([
      /(?:Issue\s*Date|Date\s*of\s*Issue)\s*[:\-]\s*([^\n]+)/i
    ]);

    const expiryDateRaw = get([
      /(?:Expiry\s*Date|Date\s*of\s*Expiry|Valid\s*Until)\s*[:\-]\s*([^\n]+)/i
    ]);

    const placeOfIssue = get([
      /(?:Place\s*of\s*Issue)\s*[:\-]\s*([^\n]+)/i
    ]);

    return {
      name,
      passportNo,
      nationality,
      sex: sexRaw.toUpperCase() === 'M' ? 'Male' : sexRaw.toUpperCase() === 'F' ? 'Female' : sexRaw,
      dob: normalizeDate(dobRaw),
      issueDate: normalizeDate(issueDateRaw),
      expiryDate: normalizeDate(expiryDateRaw),
      placeOfIssue
    };
  };

  const handlePassengerAttachmentUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const uploads = await Promise.all(Array.from(files).map(async (file) => {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = String(reader.result || '');
          resolve(result.includes(',') ? result.split(',')[1] : result);
        };
        reader.onerror = () => reject(new Error('Failed to read image file'));
        reader.readAsDataURL(file);
      });

      return {
        filename: file.name,
        mimeType: file.type || 'image/png',
        contentBase64: base64
      };
    }));

    setPassengerAttachments((prev) => [...prev, ...uploads]);
  };

  const removePassengerAttachment = (index: number) => {
    setPassengerAttachments((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const parseItineraryRows = () => {
    try {
      const itinerary = typeof tour?.itinerary === 'string'
        ? JSON.parse(tour.itinerary)
        : (tour?.itinerary || []);
      return Array.isArray(itinerary) ? itinerary : [];
    } catch {
      return [];
    }
  };

  const parseExtensions = () => {
    try {
      const raw = typeof tour?.extensions === 'string'
        ? JSON.parse(tour.extensions)
        : (tour?.extensions || []);
      return Array.isArray(raw) ? raw : [];
    } catch {
      return [];
    }
  };

  const normalizeHotelName = (value: any) => String(value || '').trim();

  const getDisplayHotels = () => {
    const set = new Set<string>();

    const directHotels = Array.isArray(tour?.hotels)
      ? tour.hotels
      : (typeof tour?.hotels === 'string' ? (() => {
          try {
            const parsed = JSON.parse(tour.hotels);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        })() : []);

    directHotels.forEach((hotel: any) => {
      const name = normalizeHotelName(hotel);
      if (name) set.add(name);
    });

    parseItineraryRows().forEach((row: any) => {
      const name = normalizeHotelName(row?.hotelName || row?.hotel);
      if (name) set.add(name);
    });

    parseExtensions().forEach((row: any) => {
      const name = normalizeHotelName(row?.hotelName || row?.hotel);
      if (name) set.add(name);
    });

    return Array.from(set);
  };

  const addOneDayToDisplayDate = (dateValue: string) => {
    const raw = String(dateValue || '').trim();
    if (!raw) return '';

    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (iso) {
      const date = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
      if (isNaN(date.getTime())) return raw;
      date.setDate(date.getDate() + 1);
      return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
    }

    const ddmmyyyy = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
    if (ddmmyyyy) {
      const date = new Date(Number(ddmmyyyy[3]), Number(ddmmyyyy[2]) - 1, Number(ddmmyyyy[1]));
      if (isNaN(date.getTime())) return raw;
      date.setDate(date.getDate() + 1);
      return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
    }

    const ddMonYY = raw.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2})$/);
    if (ddMonYY) {
      const monthMap: { [key: string]: number } = {
        Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
        Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11
      };
      const month = monthMap[ddMonYY[2]];
      if (month === undefined) return raw;
      const date = new Date(Number(`20${ddMonYY[3]}`), month, Number(ddMonYY[1]));
      if (isNaN(date.getTime())) return raw;
      date.setDate(date.getDate() + 1);
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${String(date.getDate()).padStart(2, '0')}-${monthNames[date.getMonth()]}-${String(date.getFullYear()).slice(-2)}`;
    }

    return raw;
  };

  const buildHotelBookings = (hotelName: string) => {
    const itineraryRows = parseItineraryRows();
    const extensionRows = parseExtensions();
    const normalizedHotelName = hotelName.trim().toLowerCase();

    const itineraryBookings = itineraryRows
      .filter((row: any) => String(row?.hotelName || row?.hotel || '').trim().toLowerCase() === normalizedHotelName)
      .map((row: any) => ({
        groupName: tour?.fileCode || tour?.tourName || hotelName,
        fileName: hotelName,
        checkIn: row?.date || '',
        checkOut: addOneDayToDisplayDate(row?.date || ''),
        nights: 1,
        rooms: tour?.pax || 1,
        status: row?.hotelStatus || '',
        isEdited: false
      }));

    const extensionBookings = extensionRows
      .filter((row: any) => String(row?.hotelName || row?.hotel || '').trim().toLowerCase() === normalizedHotelName)
      .map((row: any) => ({
        groupName: tour?.fileCode || tour?.tourName || hotelName,
        fileName: hotelName,
        checkIn: row?.date || '',
        checkOut: addOneDayToDisplayDate(row?.date || ''),
        nights: 1,
        rooms: 1,
        status: row?.position === 'pre' ? 'pre-extension' : row?.position === 'post' ? 'post-extension' : (row?.hotelStatus || ''),
        isEdited: false
      }));

    return [...itineraryBookings, ...extensionBookings].map((row: any) => ({
      groupName: tour?.fileCode || tour?.tourName || hotelName,
      ...row,
      isEdited: false
    }));
  };

  const openHotelBookingMail = (hotelName: string) => {
    setSelectedHotelForMail(hotelName);
    setSelectedHotelBookings(buildHotelBookings(hotelName));
    setShowBookingMailModal(true);
  };
  
  const conversations: any[] = [];
  const tickets: any[] = [];
    
  // Notes state for manager remarks
  const [notes, setNotes] = useState<any[]>([]);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Load series templates from database
  useEffect(() => {
    const loadSeriesTemplates = async () => {
      try {
        const result = await seriesTemplateAPI.getAll();
        
        console.log('📡 API Response:', result);
        if (result.success && result.data) {
          setSeriesTemplates(result.data);
          console.log('📚 Loaded series templates:', result.data);
          console.log('📋 Template operators:', result.data.map((t: any) => ({ 
            name: t.seriesName, 
            operator: t.foreignTourOperator 
          })));
        }
      } catch (error) {
        console.error('❌ Failed to load series templates:', error);
      }
    };
    
    loadSeriesTemplates();
  }, []);
  
  // Sync passengerDetails array length with tour.pax when pax changes
  useEffect(() => {
    if (tour && tour.pax) {
      const paxCount = tour.pax;
      const currentCount = passengerDetails.length;
      
      if (paxCount > currentCount) {
        // Add more passengers
        const newPassengers = Array(paxCount - currentCount).fill(null).map((_, i) => ({
          id: `passenger-${Date.now()}-${currentCount + i}`,
          roomType: '',
          roomNumber: '',
          name: '',
          dob: '',
          age: '',
          sex: '',
          nationality: '',
          passportNo: '',
          issueDate: '',
          expiryDate: '',
          placeOfIssue: '',
          dietaryRequirements: ''
        }));
        setPassengerDetails([...passengerDetails, ...newPassengers]);
      } else if (paxCount < currentCount) {
        // Remove extra passengers
        setPassengerDetails(passengerDetails.slice(0, paxCount));
      }
    }
  }, [tour?.pax]);

  // Filter templates by selected Foreign Tour Operator
  const filteredTemplates = seriesTemplates.filter(template => 
    template.foreignTourOperator === seriesFields.fileTourOperator
  );
  
  console.log('🔍 Filtering Debug:', {
    allTemplates: seriesTemplates.length,
    selectedOperator: seriesFields.fileTourOperator,
    filteredCount: filteredTemplates.length,
    filteredTemplates: filteredTemplates.map(t => t.seriesName)
  });

  useEffect(() => {
    const fetchTour = async () => {
      if (!tourId) {
        console.error('❌ No tourId provided');
        setLoading(false);
        setTour(null);
        return;
      }

      // Handle creating new tour file
      if (tourId === 'new') {
        setIsNewTour(true);
        const newTour = {
          id: `new-${Date.now()}`,
          fileCode: `TF${String(Math.floor(Math.random() * 9999)).padStart(4, '0')}`,
          tourName: '',
          clientName: '',
          clientCountry: 'India',
          pax: 1,
          startDate: new Date().toISOString().split('T')[0],
          endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          status: 'UPCOMING',
          invoiceStatus: 'YET_TO_RAISE',
          pnr: '',
          revenue: 0,
          roomNights: 0,
          manager: { name: currentUser.name, id: currentUser.id },
          agentName: currentUser.name,
          foreignTourOperator: '',
          cities: [],
          hotels: [],
          guides: [],
          guide: '',
          transportType: 'Car',
          year: new Date().getFullYear(),
          month: new Date().getMonth() + 1,
          conversations: 0,
          tickets: 0,
          tourType: 'TAILOR_MADE',
          extensions: []
        };
        setTour(newTour);
        setExtensions([]);
        setLoading(false);
        return;
      }
      
      // Load existing tour from database (API)
      try {
        setLoading(true);
        setIsNewTour(false);
        
        console.log('🔍 Fetching tour with ID:', tourId);
        console.log('🔍 Tour ID type:', typeof tourId);
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        console.log('🔍 API URL will be:', `${API_URL}/tourfiles/${tourId}`);
        
        const response = await tourFileAPI.getById(tourId);
        console.log('📥 API Response:', response);
        console.log('📥 Response success:', response?.success);
        console.log('📥 Response data:', response?.data);
        
        if (response && response.success && response.data) {
          console.log('✅ Tour data found:', response.data);
          
          // Parse JSON strings to arrays/objects with proper error handling
          const parseCities = () => {
            if (!response.data.cities) return [];
            if (Array.isArray(response.data.cities)) return response.data.cities;
            try {
              const parsed = JSON.parse(response.data.cities);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          };

          const parseHotels = () => {
            if (!response.data.hotels) return [];
            if (Array.isArray(response.data.hotels)) return response.data.hotels;
            try {
              const parsed = JSON.parse(response.data.hotels);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          };

          const parseGuides = () => {
            if (!response.data.guides) return [];
            if (Array.isArray(response.data.guides)) return response.data.guides;
            try {
              const parsed = JSON.parse(response.data.guides);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          };

          const parseItinerary = () => {
            if (!response.data.itinerary) return [];
            if (Array.isArray(response.data.itinerary)) return response.data.itinerary;
            try {
              const parsed = JSON.parse(response.data.itinerary);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          };

          const parseExtensions = () => {
            if (!response.data.extensions) return [];
            const normalizeExtensions = (items: any[]) =>
              items.map((item: any) => ({
                ...item,
                position: item?.position === 'pre' ? 'pre' : 'post'
              }));

            if (Array.isArray(response.data.extensions)) return normalizeExtensions(response.data.extensions);
            try {
              const parsed = JSON.parse(response.data.extensions);
              return Array.isArray(parsed) ? normalizeExtensions(parsed) : [];
            } catch {
              return [];
            }
          };
          
          const parsePassengerDetails = () => {
            if (!response.data.passengerDetails) {
              // Initialize based on pax count
              const paxCount = response.data.pax || 1;
              return Array(paxCount).fill(null).map((_, i) => ({
                id: `passenger-${Date.now()}-${i}`,
                roomType: '',
                roomNumber: '',
                name: '',
                dob: '',
                age: '',
                sex: '',
                nationality: '',
                passportNo: '',
                issueDate: '',
                expiryDate: '',
                placeOfIssue: '',
                dietaryRequirements: ''
              }));
            }
            if (Array.isArray(response.data.passengerDetails)) return response.data.passengerDetails;
            try {
              const parsed = JSON.parse(response.data.passengerDetails);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          };

          const parsedTour = {
            ...response.data,
            cities: parseCities(),
            hotels: parseHotels(),
            guides: parseGuides(),
            itinerary: parseItinerary()
          };
          
          // Set extensions and passenger details states
          setExtensions(parseExtensions());
          setPassengerDetails(parsePassengerDetails());
          
          console.log('📄 Parsed tour:', parsedTour);
          setTour(parsedTour);
        } else {
          console.error('❌ Tour not found - Response:', response);
          console.error('❌ Response details:', {
            success: response?.success,
            hasData: !!response?.data,
            data: response?.data
          });
          setTour(null);
        }
      } catch (error: any) {
        console.error('❌ Failed to load tour:', error);
        console.error('❌ Error message:', error?.message);
        console.error('❌ Error details:', error);
        setTour(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTour();
  }, [tourId, currentUser.name]);
  
  // Load notes when tour file is loaded
  useEffect(() => {
    const loadNotes = async () => {
      if (tour && tour.id && !tour.id.startsWith('new-')) {
        try {
          const response = await tourFileAPI.getNotes(tour.id);
          if (response.success && response.data) {
            setNotes(response.data);
          }
        } catch (error) {
          console.error('❌ Failed to load notes:', error);
        }
      }
    };
    
    loadNotes();
  }, [tour?.id]);
  
  // Handle save note
  const handleSaveNote = async () => {
    if (!newNote.trim() || !tour || !tour.id || tour.id.startsWith('new-')) return;
    
    setSavingNote(true);
    try {
      const response = await tourFileAPI.createNote(tour.id, newNote.trim());
      if (response.success && response.data) {
        setNotes([response.data, ...notes]);
        setNewNote('');
      }
    } catch (error) {
      console.error('❌ Failed to save note:', error);
      alert('Failed to save note. Please try again.');
    } finally {
      setSavingNote(false);
    }
  };
  
  // Handle delete note
  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    
    try {
      const response = await tourFileAPI.deleteNote(noteId);
      if (response.success) {
        setNotes(notes.filter(note => note.id !== noteId));
      }
    } catch (error) {
      console.error('❌ Failed to delete note:', error);
      alert('Failed to delete note. Please try again.');
    }
  };
  
  if (loading) {
    return (
      <div className="p-6">
        <Button onClick={onBack} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tour Files
        </Button>
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!tour) {
    return (
      <div className="p-6">
        <Button onClick={onBack} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tour Files
        </Button>
        <p className="mt-4 text-gray-600">Tour not found</p>
      </div>
    );
  }

  // Check if user can edit this tour
  const canEdit = currentUser.isAdmin || tour.manager?.name === currentUser.name;

  const getStatusColor = (status: string) => {
    const normalizedStatus = status?.toLowerCase();
    switch (normalizedStatus) {
      case 'completed': return 'bg-green-100 text-green-700 border-green-300';
      case 'in-progress':
      case 'in_progress': return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'upcoming': return 'bg-blue-100 text-blue-700 border-blue-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const handleGeneratePDF = () => {
    // Add a class to body to indicate print mode for Tour File Cover only
    document.body.classList.add('printing-tour-cover');
    
    // Print the page - browser will handle PDF generation
    // The CSS will ensure only the Tour File Cover is visible
    window.print();
    
    // Remove the class after print dialog closes
    setTimeout(() => {
      document.body.classList.remove('printing-tour-cover');
    }, 1000);
  };

  const handleDelete = async () => {
    if (!tour || isNewTour) return;
    
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${tour.tourName}"?\n\nThis will permanently remove this tour file from the database.`
    );
    
    if (!confirmDelete) return;
    
    try {
      console.log('🗑️ Deleting tour file:', tour.id);
      const response = await tourFileAPI.delete(tour.id);
      
      if (response.success) {
        alert('✅ Tour file deleted successfully!');
        // Dispatch event to refresh tour list
        window.dispatchEvent(new Event('tourFilesSaved'));
        // Go back to tour files list
        onBack();
      } else {
        alert('❌ Failed to delete tour file');
      }
    } catch (error: any) {
      console.error('Delete error:', error);
      alert(`❌ Error deleting tour file: ${error.message || 'Unknown error'}`);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <Button onClick={onBack} variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tour Files
          </Button>
          <h1 className="text-2xl font-semibold">{isNewTour ? 'Create New Tour File' : (tour.tourName || 'Tour File')}</h1>
          <div className="flex items-center gap-3">
            {tour.pnr && <span className="text-sm text-gray-600">PNR: {tour.pnr}</span>}
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button onClick={handleGeneratePDF} className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700">
            <Download className="w-4 h-4 mr-2" />
            Generate Complete PDF
          </Button>
        </div>
      </div>

      {/* Tour Type Selection (only for new tours) */}
      {isNewTour && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold mb-4">Select Tour Type</h3>
          <div className="flex gap-4">
            <button
              onClick={() => {
                setTourType('TAILOR_MADE');
                setTour({ ...tour, tourType: 'TAILOR_MADE' });
              }}
              className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                tourType === 'TAILOR_MADE'
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="text-center">
                <FileText className={`w-8 h-8 mx-auto mb-2 ${
                  tourType === 'TAILOR_MADE' ? 'text-purple-600' : 'text-gray-400'
                }`} />
                <p className={`font-medium ${
                  tourType === 'TAILOR_MADE' ? 'text-purple-900' : 'text-gray-700'
                }`}>Tailor Made</p>
                <p className="text-xs text-gray-500 mt-1">Custom tour for individual clients</p>
              </div>
            </button>

            <button
              onClick={() => {
                setTourType('SERIES');
                setSeriesMode(null); // Reset mode selection
                setTour({ 
                  ...tour, 
                  tourType: 'SERIES',
                  tourName: seriesFields.fileName,
                  clientCountry: seriesFields.country,
                  cities: seriesFields.city ? [seriesFields.city] : [],
                  agentName: seriesFields.fileManager,
                  year: seriesFields.year,
                  month: seriesFields.month,
                  foreignTourOperator: seriesFields.fileTourOperator
                });
              }}
              className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                tourType === 'SERIES'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <div className="text-center">
                <Users className={`w-8 h-8 mx-auto mb-2 ${
                  tourType === 'SERIES' ? 'text-blue-600' : 'text-gray-400'
                }`} />
                <p className={`font-medium ${
                  tourType === 'SERIES' ? 'text-blue-900' : 'text-gray-700'
                }`}>Series</p>
                <p className="text-xs text-gray-500 mt-1">Pre-planned tour series</p>
              </div>
            </button>
          </div>
        </motion.div>
      )}

      {/* Series Mode Selection (Single vs Multiple) */}
      {isNewTour && tourType === 'SERIES' && seriesMode === null && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold mb-4">Select Creation Mode</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => setSeriesMode('single')}
              className="p-6 rounded-lg border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all"
            >
              <div className="text-center">
                <FileText className="w-10 h-10 mx-auto mb-3 text-blue-600" />
                <p className="font-semibold text-gray-900 mb-2">Single File</p>
                <p className="text-sm text-gray-600">Create one tour file with complete details</p>
              </div>
            </button>
            <button
              onClick={() => setSeriesMode('multiple')}
              className="p-6 rounded-lg border-2 border-gray-200 hover:border-green-500 hover:bg-green-50 transition-all"
            >
              <div className="text-center">
                <Users className="w-10 h-10 mx-auto mb-3 text-green-600" />
                <p className="font-semibold text-gray-900 mb-2">Multiple Files</p>
                <p className="text-sm text-gray-600">Create multiple tour files at once with different arrival dates</p>
              </div>
            </button>
          </div>
        </motion.div>
      )}

      {/* Series Fields (only shown for Series type after mode is selected) */}
      {isNewTour && tourType === 'SERIES' && seriesMode !== null && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold mb-4">Series Tour Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Row 1: Foreign Tour Operator | Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Foreign Tour Operator
              </label>
              <select
                value={seriesFields.fileTourOperator}
                onChange={(e) => {
                  const selectedOperator = e.target.value;
                  // Auto-fill country based on operator
                  const autoCountry = OPERATOR_COUNTRIES[selectedOperator] || '';
                  const newFields = { 
                    ...seriesFields, 
                    fileTourOperator: selectedOperator,
                    country: autoCountry // Auto-fill country
                  };
                  setSeriesFields(newFields);
                  // Auto-update tour object
                  setTour((prev: any) => ({ 
                    ...prev, 
                    foreignTourOperator: selectedOperator,
                    clientCountry: autoCountry
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select operator...</option>
                {(() => {
                  const allOperators = [
                    'Venus Travel', 'Viva Holidays', 'Inspiring Vacations', 'Sanskriti Tours',
                    'Soulful Safaris', 'Direct Client', 'Referral Client', '24x7 Rooms com',
                    'Trip Tailor', 'Muziris Heritage', 'Explore', 'Fest Travel', 'Japan', 'ROCS',
                    'Buckenham Travel', 'Celebration India Holidays', 'Hooting Owls',
                    'Royal Mountain Tours', 'Flywell Tours', 'Destinations World wide',
                    'Business Promotion / FAM'
                  ];
                  const ops = (!currentUser.isAdmin && currentUser.assignedOperators && currentUser.assignedOperators.length > 0)
                    ? allOperators.filter(op => currentUser.assignedOperators!.includes(op))
                    : allOperators;
                  return ops.map(op => <option key={op} value={op}>{op}</option>);
                })()}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country
              </label>
              <input
                type="text"
                value={seriesFields.country}
                onChange={(e) => {
                  const newFields = { ...seriesFields, country: e.target.value };
                  setSeriesFields(newFields);
                  // Auto-update tour object
                  setTour((prev: any) => ({ ...prev, clientCountry: e.target.value }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter country"
              />
            </div>

            {/* Row 2: File Manager | City */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                File Manager
              </label>
              <input
                type="text"
                value={seriesFields.fileManager}
                onChange={(e) => {
                  const newFields = { ...seriesFields, fileManager: e.target.value };
                  setSeriesFields(newFields);
                  // Auto-update tour object
                  setTour((prev: any) => ({ ...prev, agentName: e.target.value }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Manager name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City
              </label>
              <input
                type="text"
                value={seriesFields.city}
                onChange={(e) => {
                  const newFields = { ...seriesFields, city: e.target.value };
                  setSeriesFields(newFields);
                  // Auto-update tour object
                  setTour((prev: any) => ({ 
                    ...prev, 
                    cities: e.target.value ? [e.target.value] : [] 
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter city"
              />
            </div>
          </div>

          {/* Row 3: File Name | Year | Month (Month only for single file) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                File Name <span className="text-red-500">*</span>
              </label>
              <select
                value={seriesFields.fileName}
                onChange={(e) => {
                  const newFields = { ...seriesFields, fileName: e.target.value };
                  setSeriesFields(newFields);
                  // Auto-update tour object for real-time preview
                  setTour((prev: any) => ({
                    ...prev,
                    tourName: e.target.value
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a series...</option>
                {!seriesFields.fileTourOperator && (
                  <option value="" disabled>Please select Foreign Tour Operator first</option>
                )}
                {seriesFields.fileTourOperator && filteredTemplates.length === 0 && (
                  <option value="" disabled>No templates available for this operator</option>
                )}
                {filteredTemplates.map(template => (
                  <option key={template.id} value={template.seriesName}>
                    {template.seriesName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Year
              </label>
              <input
                type="number"
                value={seriesFields.year}
                onChange={(e) => {
                  const yearValue = parseInt(e.target.value);
                  const newFields = { ...seriesFields, year: yearValue };
                  setSeriesFields(newFields);
                  // Auto-update tour object
                  setTour((prev: any) => ({ ...prev, year: yearValue }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="2026"
              />
            </div>

            {/* Month field - only shown for single file mode */}
            {seriesMode === 'single' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Month
                </label>
                <select
                  value={seriesFields.month}
                onChange={(e) => {
                  const monthValue = parseInt(e.target.value);
                  const newFields = { ...seriesFields, month: monthValue };
                  setSeriesFields(newFields);
                  // Auto-update tour object
                  setTour((prev: any) => ({ ...prev, month: monthValue }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={1}>January</option>
                <option value={2}>February</option>
                <option value={3}>March</option>
                <option value={4}>April</option>
                <option value={5}>May</option>
                <option value={6}>June</option>
                <option value={7}>July</option>
                <option value={8}>August</option>
                <option value={9}>September</option>
                <option value={10}>October</option>
                <option value={11}>November</option>
                <option value={12}>December</option>
                </select>
              </div>
            )}
          </div>

          {/* Arrival Dates field - only for multiple files mode */}
          {seriesMode === 'multiple' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Arrival Dates <span className="text-red-500">*</span>
                <span className="text-xs text-gray-500 ml-2">(Click on calendar dates to select multiple dates)</span>
              </label>
              
              {/* Multi-Select Calendar */}
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => setShowCalendar(!showCalendar)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-left bg-white hover:bg-gray-50 flex items-center justify-between"
                >
                  <span className="text-gray-700">
                    {selectedDates.length === 0 ? 'Click to open calendar' : `${selectedDates.length} date(s) selected`}
                  </span>
                  <Calendar className="h-5 w-5 text-gray-400" />
                </button>
                
                {/* Calendar Popup */}
                {showCalendar && (
                  <div className="border border-gray-300 rounded-lg p-4 bg-white shadow-lg">
                    <DayPicker
                      mode="multiple"
                      selected={selectedDates}
                      onSelect={(dates) => {
                        const sortedDates = (dates || []).sort((a, b) => a.getTime() - b.getTime());
                        setSelectedDates(sortedDates);
                        
                        // Update arrivalDates string
                        const dateStrings = sortedDates.map(d => {
                          const day = String(d.getDate()).padStart(2, '0');
                          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                          const month = monthNames[d.getMonth()];
                          const year = String(d.getFullYear()).slice(-2);
                          return `${day}-${month}-${year}`;
                        });
                        setArrivalDates(dateStrings.join(', '));
                      }}
                      className="rdp-custom"
                    />
                    <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
                      <button
                        type="button"
                        onClick={() => setShowCalendar(false)}
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Selected Dates Display */}
                {selectedDates.length > 0 && (
                  <div className="border border-gray-300 rounded-md p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Selected Dates ({selectedDates.length}):</span>
                      <button
                        onClick={() => {
                          setSelectedDates([]);
                          setArrivalDates('');
                        }}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedDates.map((date, idx) => {
                        const day = String(date.getDate()).padStart(2, '0');
                        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                        const month = monthNames[date.getMonth()];
                        const year = String(date.getFullYear()).slice(-2);
                        const dateStr = `${day}-${month}-${year}`;
                        
                        return (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-md text-sm"
                          >
                            {dateStr}
                            <button
                              onClick={() => {
                                const newDates = selectedDates.filter((_, i) => i !== idx);
                                setSelectedDates(newDates);
                                const dateStrings = newDates.map(d => {
                                  const day = String(d.getDate()).padStart(2, '0');
                                  const month = monthNames[d.getMonth()];
                                  const year = String(d.getFullYear()).slice(-2);
                                  return `${day}-${month}-${year}`;
                                });
                                setArrivalDates(dateStrings.join(', '));
                              }}
                              className="ml-1 text-blue-600 hover:text-blue-800 font-bold"
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-gray-500">
                  {selectedDates.length} dates selected - Will create {selectedDates.length} tour files
                </p>
              </div>
            </div>
          )}

          {/* Create Multiple Files Button */}
          {seriesMode === 'multiple' && (
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setSeriesMode(null);
                  setArrivalDates('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!seriesFields.fileName || !seriesFields.fileTourOperator || !arrivalDates.trim()) {
                    alert('Please fill in File Name, Foreign Tour Operator, and Arrival Dates');
                    return;
                  }

                  const dates = arrivalDates.split(',').map(d => d.trim()).filter(d => d);
                  if (dates.length === 0) {
                    alert('Please enter at least one arrival date');
                    return;
                  }

                  const confirmed = confirm(`Create ${dates.length} tour files with the entered details?`);
                  if (!confirmed) return;

                  try {
                    console.log('🎯 Creating', dates.length, 'tour files...');
                    
                    // Load series template from database
                    console.log('📚 Loading template for:', seriesFields.fileName);
                    const templatesResponse = await seriesTemplateAPI.getAll();
                    let templateItinerary: any[] = [];
                    
                    if (templatesResponse.success && templatesResponse.data) {
                      const matchedTemplate = templatesResponse.data.find((t: any) => 
                        t.seriesName.toLowerCase().trim() === seriesFields.fileName.toLowerCase().trim()
                      );
                      
                      if (matchedTemplate) {
                        // Parse itinerary if it's a string
                        templateItinerary = typeof matchedTemplate.itineraryTemplate === 'string' 
                          ? JSON.parse(matchedTemplate.itineraryTemplate)
                          : matchedTemplate.itineraryTemplate;
                        console.log('✅ Template found with', templateItinerary?.length || 0, 'rows');
                      } else {
                        console.warn('⚠️ No template found for:', seriesFields.fileName);
                      }
                    }
                    
                    const createdTours = [];

                    for (let i = 0; i < dates.length; i++) {
                      // IMPORTANT: Use getNextSequence API which counts ALL files globally (not access-controlled)
                      let currentSequence = 1;
                      try {
                        const seqResponse = await tourFileAPI.getNextSequence(seriesFields.fileName.trim());
                        if (seqResponse && seqResponse.success) {
                          currentSequence = seqResponse.data.nextNumber;
                        }
                      } catch (seqError) {
                        console.error('Failed to get sequence, falling back to getAll:', seqError);
                        const freshToursResponse = await tourFileAPI.getAll();
                        const freshExistingTours = freshToursResponse?.data || [];
                        const toursWithSameName = freshExistingTours.filter((t: any) => 
                          t.tourName && t.tourName.toLowerCase().trim() === seriesFields.fileName.toLowerCase().trim()
                        );
                        currentSequence = toursWithSameName.length + 1;
                      }
                      
                      const dateInput = dates[i];
                      
                      // Parse dd-mmm-yy format to YYYY-MM-DD
                      const arrivalDate = parseDateFormat(dateInput);
                      if (!arrivalDate) {
                        alert(`Invalid date format: "${dateInput}". Please use dd-mmm-yy format (e.g., 12-Feb-26)`);
                        return;
                      }
                      
                      // Extract month and year from arrival date for file code
                      const arrivalDateObj = new Date(arrivalDate);
                      const monthFromDate = arrivalDateObj.getMonth() + 1; // 1-12
                      const yearFromDate = arrivalDateObj.getFullYear();
                      
                      // Generate file code prefix with month from arrival date
                      const cityCode = CITY_CODES[seriesFields.city] || 'XX';
                      const managerCode = FILE_MANAGER_CODES[seriesFields.fileManager] || 'XX';
                      const monthCode = String(monthFromDate).padStart(2, '0');
                      const yearCode = String(yearFromDate).slice(-2);
                      const operatorCode = AGENT_CODES[seriesFields.fileTourOperator] || 'XX';
                      const codePrefix = `${cityCode}${managerCode}${monthCode}${yearCode}${operatorCode}`;
                      
                      // Use fresh sequence number
                      const fileCode = `${codePrefix}${String(currentSequence).padStart(4, '0')}`;
                      
                      console.log('🏷️ Generated file code:', {
                        tourNumber: i + 1,
                        totalTours: dates.length,
                        newSequence: currentSequence,
                        prefix: codePrefix,
                        fileCode: fileCode
                      });
                      
                      // Use template from database
                      let itinerary = [];
                      if (templateItinerary && templateItinerary.length > 0) {
                        const startDate = new Date(arrivalDate);
                        itinerary = templateItinerary.map((item: any, idx: number) => {
                          const rowDate = new Date(startDate);
                          rowDate.setDate(startDate.getDate() + idx);
                          const day = String(rowDate.getDate()).padStart(2, '0');
                          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                          const month = monthNames[rowDate.getMonth()];
                          const year = String(rowDate.getFullYear()).slice(-2);
                          const formattedDate = `${day}-${month}-${year}`;

                          return {
                            date: formattedDate,
                            sector: item.sector || '',
                            flightNumber: item.flightNumber || '',
                            time: item.time || '',
                            status: '',
                            city: item.city || '',
                            hotelName: item.hotelName || '',
                            hotelStatus: '',
                            roomCategory: '',
                            roomTicked: false
                          };
                        });
                      } else {
                        console.warn('⚠️ No template itinerary available for:', seriesFields.fileName);
                      }

                      // Calculate endDate from itinerary (last row's date)
                      let endDate = arrivalDate; // Default to arrival date
                      if (itinerary.length > 0) {
                        // Use the very last row's date (including "Depart" rows)
                        const lastRow = itinerary[itinerary.length - 1];
                        if (lastRow && lastRow.date) {
                          endDate = parseDateFormat(lastRow.date) || arrivalDate;
                        }
                      }

                      const tourData = {
                        fileCode: fileCode, // Provide the generated file code
                        tourType: 'SERIES',
                        tourName: seriesFields.fileName.trim(),
                        clientCountry: seriesFields.country,
                        cities: seriesFields.city ? [seriesFields.city] : [],
                        agentName: seriesFields.fileManager,
                        year: yearFromDate, // Use year from arrival date
                        month: monthFromDate, // Use month from arrival date
                        foreignTourOperator: seriesFields.fileTourOperator,
                        startDate: arrivalDate,
                        endDate: endDate, // Add calculated end date
                        itinerary: itinerary,
                        status: 'UPCOMING'
                      };

                      const response = await tourFileAPI.create(tourData);
                      createdTours.push(response.data);
                      console.log(`✅ Created tour ${i + 1}/${dates.length}:`, fileCode);
                    }

                    alert(`Successfully created ${createdTours.length} tour files!\n\nFile Codes:\n${createdTours.map((t: any) => t.fileCode).join('\n')}`);
                    onBack();
                  } catch (error) {
                    console.error('❌ Error creating tours:', error);
                    alert('Failed to create tour files. Please try again.');
                  }
                }}
                className="bg-green-600 hover:bg-green-700"
              >
                Create {arrivalDates.split(',').filter(d => d.trim()).length} Tour Files
              </Button>
            </div>
          )}
        </motion.div>
      )}

      {/* File Cover Preview - Only show for Tailor Made OR Series Single File mode */}
      {(!isNewTour || tourType === 'TAILOR_MADE' || (tourType === 'SERIES' && seriesMode === 'single')) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-8"
        >
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold mb-2">Tour File Cover</h2>
            <p className="text-sm text-gray-500">
              {isNewTour 
                ? 'Fill in the tour details below and click "Save Changes" to create the tour file' 
                : 'Tour file details - Click "Edit Cover" to modify'}
            </p>
          </div>

          <TourFileCover 
          tour={tour} 
          canEdit={true}
          currentUser={currentUser}
          extensions={extensions}
          passengerDetails={passengerDetails}
          onEditingChange={(isEditing) => setIsEditingCover(isEditing)}
          onPaxChange={(newPax) => {
            // Sync passenger details count with new pax
            const currentCount = passengerDetails.length;
            if (newPax > currentCount) {
              const newPassengers = Array(newPax - currentCount).fill(null).map((_, i) => ({
                id: `passenger-${Date.now()}-${currentCount + i}`,
                roomType: '',
                roomNumber: '',
                name: '',
                dob: '',
                age: '',
                sex: '',
                nationality: '',
                passportNo: '',
                issueDate: '',
                expiryDate: '',
                placeOfIssue: '',
                dietaryRequirements: ''
              }));
              setPassengerDetails([...passengerDetails, ...newPassengers]);
            } else if (newPax < currentCount) {
              setPassengerDetails(passengerDetails.slice(0, newPax));
            }
          }}
          onUpdate={(updatedTour) => {
            // Update the tour state with the data from the database
            setTour(updatedTour);
            
            // Parse and set extensions from the updated tour
            if (updatedTour.extensions) {
              try {
                const parsedExtensions = typeof updatedTour.extensions === 'string' 
                  ? JSON.parse(updatedTour.extensions) 
                  : updatedTour.extensions;
                setExtensions(
                  Array.isArray(parsedExtensions)
                    ? parsedExtensions.map((item: any) => ({
                        ...item,
                        position: item?.position === 'pre' ? 'pre' : 'post'
                      }))
                    : []
                );
              } catch (e) {
                console.error('Failed to parse extensions:', e);
              }
            }
            
            // Parse and set passenger details from the updated tour
            if (updatedTour.passengerDetails) {
              try {
                const parsedPassengers = typeof updatedTour.passengerDetails === 'string' 
                  ? JSON.parse(updatedTour.passengerDetails) 
                  : updatedTour.passengerDetails;
                setPassengerDetails(parsedPassengers);
              } catch (e) {
                console.error('Failed to parse passenger details:', e);
              }
            }
            
            // If this was a new tour, it now has a real database ID
            if (isNewTour) {
              setIsNewTour(false);
            }
          }}
          seriesFields={tourType === 'SERIES' ? seriesFields : undefined}
        />
        </motion.div>
      )}

      {/* Extensions Section - Button and List */}
      {(!isNewTour || tourType === 'TAILOR_MADE' || (tourType === 'SERIES' && seriesMode === 'single')) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Extensions</h3>
            <button
              onClick={() => {
                if (!isEditingCover) {
                  alert('Please click "Edit Cover" first to add extensions');
                  return;
                }
                setCurrentExtension({
                  id: '',
                  passengerName: '',
                  position: 'post',
                  date: '',
                  sector: '',
                  flightNumber: '',
                  time: '',
                  status: '',
                  guideName: '',
                  guideType: '',
                  city: '',
                  hotelName: '',
                  hotelStatus: '',
                  roomCategory: '',
                  meals: ['Breakfast'],
                  breakfastRemark: '',
                  lunchRemark: '',
                  dinnerRemark: ''
                });
                setExtensionPosition('post');
                setShowExtensionForm(true);
              }}
              disabled={!isEditingCover}
              className={`px-4 py-2 rounded-md flex items-center gap-2 font-medium transition-all ${
                isEditingCover 
                  ? 'bg-purple-600 text-white hover:bg-purple-700' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <span className="text-lg">+</span> Add Extension
            </button>
          </div>

          {/* List of Extensions */}
          {extensions.length > 0 ? (
            <ul className="space-y-2">
              {/* Group extensions by passenger name + placement */}
              {Array.from(new Set(extensions.map(e => `${e.passengerName}::${e.position || 'post'}`))).map((groupKey) => {
                const [passengerName, position] = groupKey.split('::');
                const passengerExtensions = extensions.filter(
                  e => e.passengerName === passengerName && (e.position || 'post') === position
                );
                
                // Get date range from extensions
                const extensionDates = passengerExtensions
                  .map(e => e.date)
                  .filter(d => d && d.trim())
                  .sort();
                
                let dateDisplay = '';
                if (extensionDates.length === 0) {
                  dateDisplay = `(${passengerExtensions.length} row${passengerExtensions.length > 1 ? 's' : ''})`;
                } else if (extensionDates.length === 1) {
                  dateDisplay = extensionDates[0];
                } else {
                  // Show first and last date
                  dateDisplay = `${extensionDates[0]} - ${extensionDates[extensionDates.length - 1]}`;
                }
                
                return (
                  <li key={groupKey}>
                    <button
                      onClick={() => {
                        setCurrentExtension(passengerExtensions[0]);
                        setExtensionPassengerName(passengerName);
                        setExtensionRows(passengerExtensions);
                        setExtensionPosition(position === 'pre' ? 'pre' : 'post');
                        setShowExtensionForm(true);
                      }}
                      className="w-full text-left px-4 py-3 rounded-md border transition-colors bg-gray-50 hover:bg-gray-100 border-gray-200 cursor-pointer"
                    >
                      <span className="flex items-center justify-between gap-3">
                        <span className="flex-1">
                          <span className="text-purple-600 font-medium">• {passengerName}</span>
                          <span className="text-gray-500 ml-3 text-sm">{dateDisplay}</span>
                          <span className={`ml-3 text-xs px-2 py-0.5 rounded ${position === 'pre' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                            {position === 'pre' ? 'Pre Extension' : 'Post Extension'}
                          </span>
                        </span>
                        {isEditingCover && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              setExtensions((prev) => prev.filter((extension) =>
                                !(extension.passengerName === passengerName && (extension.position || 'post') === position)
                              ));
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-50 text-red-600 hover:bg-red-100 text-xs font-medium"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Delete
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-gray-500 text-sm text-center py-8">
              {isEditingCover 
                ? 'No extensions added yet. Click "+ Add Extension" to add one.' 
                : 'No extensions added yet. Click "Edit Cover" to add extensions.'}
            </p>
          )}
        </motion.div>
      )}

      {/* Extension Form Modal */}
      {showExtensionForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold">
                {currentExtension.id ? (isEditingCover ? 'Edit Extension' : 'View Extension') : 'Add Extension'}
              </h3>
              <div className="flex items-center gap-3">
                {isEditingCover && (
                  <button
                    onClick={() => {
                      // Add new row without passenger name
                      setExtensionRows([...extensionRows, {
                        id: Date.now().toString(),
                        passengerName: '',
                        position: extensionPosition,
                        date: '',
                        sector: '',
                        flightNumber: '',
                        time: '',
                        status: '',
                        guideName: '',
                        guideType: '',
                        city: '',
                        hotelName: '',
                        hotelStatus: '',
                        roomCategory: '',
                        meals: ['Breakfast'],
                        breakfastRemark: '',
                        lunchRemark: '',
                        dinnerRemark: ''
                      }]);
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm font-medium flex items-center gap-2"
                  >
                    <span>+</span>
                    Add Row
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowExtensionForm(false);
                    setExtensionPassengerName('');
                    setExtensionPosition('post');
                    // Reset to single row
                    setExtensionRows([{
                      id: '1',
                      passengerName: '',
                      position: 'post',
                      date: '',
                      sector: '',
                      flightNumber: '',
                      time: '',
                      status: '',
                      guideName: '',
                      guideType: '',
                      city: '',
                      hotelName: '',
                      hotelStatus: '',
                      roomCategory: '',
                      meals: ['Breakfast'],
                      breakfastRemark: '',
                      lunchRemark: '',
                      dinnerRemark: ''
                    }]);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Passenger Name Input - Single for all rows */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Passenger Name *
                </label>
                <input
                  type="text"
                  value={extensionPassengerName}
                  onChange={(e) => setExtensionPassengerName(e.target.value)}
                  placeholder="Enter passenger name for this extension"
                  disabled={!isEditingCover}
                  className="w-full max-w-md px-3 py-2 border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100 disabled:border-gray-300"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This name will apply to all rows in this extension
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Extension Placement</label>
                <select
                  value={extensionPosition}
                  onChange={(e) => setExtensionPosition(e.target.value === 'pre' ? 'pre' : 'post')}
                  disabled={!isEditingCover}
                  className="w-full max-w-xs px-3 py-2 border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100 disabled:border-gray-300"
                >
                  <option value="pre">Pre Extension (before main itinerary)</option>
                  <option value="post">Post Extension (after main itinerary)</option>
                </select>
              </div>

              <div className="border-2 border-gray-400 rounded-lg overflow-hidden">
                {/* Header */}
                <div className="grid grid-cols-[0.8fr_0.6fr_1fr_3fr_1.5fr_1.3fr_3.5fr_1fr_1.2fr] border-b border-gray-400 bg-gray-100">
                  <div className="border-r border-gray-400 px-2 py-2 text-center font-medium text-sm">Date</div>
                  <div className="border-r border-gray-400 px-2 py-2 text-center font-medium text-sm">Day</div>
                  <div className="border-r border-gray-400 px-2 py-2 text-center font-medium text-sm">Sector</div>
                  <div className="border-r border-gray-400 px-2 py-2 text-center font-medium text-sm">Flights / Train</div>
                  <div className="border-r border-gray-400 px-2 py-2 text-center font-medium text-sm">Guide</div>
                  <div className="border-r border-gray-400 px-2 py-2 text-center font-medium text-sm">City</div>
                  <div className="border-r border-gray-400 px-2 py-2 text-center font-medium text-sm">Hotel Reservation</div>
                  <div className="border-r border-gray-400 px-2 py-2 text-center font-medium text-sm">Room Category</div>
                  <div className="px-2 py-2 text-center font-medium text-sm">Meals</div>
                </div>

                {/* Sub Header */}
                <div className="grid grid-cols-[0.8fr_0.6fr_1fr_1fr_1fr_1fr_1fr_0.5fr_1.3fr_2.7fr_0.8fr_1fr_1.2fr] border-b border-gray-400 bg-gray-50 [&>div]:min-w-0 [&>div]:overflow-hidden">
                  <div className="col-span-3 border-r border-gray-400"></div>
                  <div className="border-r border-gray-400 px-2 py-1 text-center text-xs">Number</div>
                  <div className="border-r border-gray-400 px-2 py-1 text-center text-xs">Time</div>
                  <div className="border-r border-gray-400 px-2 py-1 text-center text-xs">Status</div>
                  <div className="border-r border-gray-400 px-2 py-1 text-center text-xs">Name</div>
                  <div className="border-r border-gray-400 px-2 py-1 text-center text-xs">Type</div>
                  <div className="border-r border-gray-400"></div>
                  <div className="border-r border-gray-400 px-2 py-1 text-center text-xs">Hotel Name</div>
                  <div className="border-r border-gray-400 px-2 py-1 text-center text-xs">Status</div>
                  <div className="border-r border-gray-400"></div>
                  <div className="px-1 py-1 text-center text-xs">B/L/D</div>
                </div>

                {/* Data Rows - Map through extensionRows */}
                {extensionRows.map((row, rowIndex) => (
                  <div key={row.id} className="grid grid-cols-[0.8fr_0.6fr_1fr_1fr_1fr_1fr_1fr_0.5fr_1.3fr_2.7fr_0.8fr_1fr_1.2fr] min-h-[45px] border-b border-gray-200 last:border-b-0 [&>div]:min-w-0 [&>div]:overflow-hidden">
                    {/* Date */}
                    <div className="border-r border-gray-300 px-2 py-2">
                      <input
                        type="text"
                        value={row.date}
                        onChange={(e) => {
                          const updated = [...extensionRows];
                          updated[rowIndex].date = e.target.value;
                          setExtensionRows(updated);
                        }}
                        placeholder="dd-mmm-yy"
                        disabled={!isEditingCover}
                        className="w-full px-2 py-1 rounded-md focus:outline-none text-sm bg-white border-2 border-blue-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-200 disabled:bg-white disabled:border-0 disabled:cursor-default disabled:px-0"
                      />
                    </div>
                    {/* Day */}
                    <div className="border-r border-gray-300 px-2 py-2 flex items-center justify-center">
                      <span className="text-sm font-medium">
                        {(() => {
                          if (!row.date) return '';
                          try {
                            const parts = row.date.split('-');
                            if (parts.length !== 3) return '';
                            const day = parseInt(parts[0]);
                            const monthMap: {[key: string]: number} = {
                              'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
                              'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
                            };
                            const month = monthMap[parts[1]];
                            const year = parseInt('20' + parts[2]);
                            if (month === undefined || isNaN(day) || isNaN(year)) return '';
                            const date = new Date(year, month, day);
                            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                            return dayNames[date.getDay()];
                          } catch {
                            return '';
                          }
                        })()}
                      </span>
                    </div>
                    {/* Sector */}
                    <div className="border-r border-gray-300 px-2 py-2">
                      <input
                        type="text"
                        value={row.sector}
                        onChange={(e) => {
                          const updated = [...extensionRows];
                          updated[rowIndex].sector = e.target.value;
                          setExtensionRows(updated);
                        }}
                        disabled={!isEditingCover}
                        className="w-full px-2 py-1 rounded-md focus:outline-none text-sm bg-white border-2 border-blue-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-200 disabled:bg-white disabled:border-0 disabled:cursor-default disabled:px-0"
                      />
                    </div>
                    {/* Flight Number */}
                    <div className="border-r border-gray-300 px-2 py-2">
                      <input
                        type="text"
                        value={row.flightNumber}
                        onChange={(e) => {
                          const updated = [...extensionRows];
                          updated[rowIndex].flightNumber = e.target.value;
                          setExtensionRows(updated);
                        }}
                        disabled={!isEditingCover}
                        className="w-full px-2 py-1 rounded-md focus:outline-none text-sm bg-white border-2 border-blue-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-200 disabled:bg-white disabled:border-0 disabled:cursor-default disabled:px-0"
                      />
                    </div>
                    {/* Time */}
                    <div className="border-r border-gray-300 px-2 py-2">
                      <input
                        type="text"
                        value={row.time}
                        onChange={(e) => {
                          const updated = [...extensionRows];
                          updated[rowIndex].time = e.target.value;
                          setExtensionRows(updated);
                        }}
                        disabled={!isEditingCover}
                        className="w-full px-2 py-1 rounded-md focus:outline-none text-sm bg-white border-2 border-blue-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-200 disabled:bg-white disabled:border-0 disabled:cursor-default disabled:px-0"
                      />
                    </div>
                    {/* Status */}
                    <div className="border-r border-gray-300 px-2 py-2">
                      <input
                        type="text"
                        value={row.status}
                        onChange={(e) => {
                          const updated = [...extensionRows];
                          updated[rowIndex].status = e.target.value;
                          setExtensionRows(updated);
                        }}
                        disabled={!isEditingCover}
                        className="w-full px-2 py-1 rounded-md focus:outline-none text-sm bg-white border-2 border-blue-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-200 disabled:bg-white disabled:border-0 disabled:cursor-default disabled:px-0"
                      />
                    </div>
                    {/* Guide Name */}
                    <div className="border-r border-gray-300 px-2 py-2">
                      <input
                        type="text"
                        value={row.guideName || ''}
                        onChange={(e) => {
                          const updated = [...extensionRows];
                          updated[rowIndex].guideName = e.target.value;
                          setExtensionRows(updated);
                        }}
                        placeholder="Guide name"
                        disabled={!isEditingCover}
                        className="w-full px-2 py-1 rounded-md focus:outline-none text-xs bg-white border-2 border-blue-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-200 disabled:bg-white disabled:border-0 disabled:cursor-default disabled:px-0"
                      />
                    </div>
                    {/* Guide Type */}
                    <div className="border-r border-gray-300 px-2 py-2">
                      <select
                        value={row.guideType || ''}
                        onChange={(e) => {
                          const updated = [...extensionRows];
                          updated[rowIndex].guideType = e.target.value;
                          setExtensionRows(updated);
                        }}
                        disabled={!isEditingCover}
                        className="w-full px-1 py-1 rounded-md focus:outline-none text-xs bg-white border-2 border-blue-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-200 disabled:bg-white disabled:border-0 disabled:cursor-default disabled:px-0"
                      >
                        <option value="">-</option>
                        <option value="F.D">F.D</option>
                        <option value="H.D">H.D</option>
                      </select>
                    </div>
                    {/* City */}
                    <div className="border-r border-gray-300 px-2 py-2">
                      <input
                        type="text"
                        value={row.city}
                        onChange={(e) => {
                          const updated = [...extensionRows];
                          updated[rowIndex].city = e.target.value;
                          setExtensionRows(updated);
                        }}
                        disabled={!isEditingCover}
                        className="w-full px-2 py-1 rounded-md focus:outline-none text-sm bg-white border-2 border-blue-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-200 disabled:bg-white disabled:border-0 disabled:cursor-default disabled:px-0"
                      />
                    </div>
                    {/* Hotel Name */}
                    <div className="border-r border-gray-300 px-2 py-2">
                      <input
                        type="text"
                        value={row.hotelName}
                        onChange={(e) => {
                          const updated = [...extensionRows];
                          updated[rowIndex].hotelName = e.target.value;
                          setExtensionRows(updated);
                        }}
                        disabled={!isEditingCover}
                        className="w-full px-2 py-1 rounded-md focus:outline-none text-sm bg-white border-2 border-blue-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-200 disabled:bg-white disabled:border-0 disabled:cursor-default disabled:px-0"
                      />
                    </div>
                    {/* Hotel Status */}
                    <div className="border-r border-gray-300 px-2 py-2">
                      <input
                        type="text"
                        value={row.hotelStatus}
                        onChange={(e) => {
                          const updated = [...extensionRows];
                          updated[rowIndex].hotelStatus = e.target.value;
                          setExtensionRows(updated);
                        }}
                        disabled={!isEditingCover}
                        className="w-full px-2 py-1 rounded-md focus:outline-none text-sm bg-white border-2 border-blue-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-200 disabled:bg-white disabled:border-0 disabled:cursor-default disabled:px-0"
                      />
                    </div>
                    {/* Room Category */}
                    <div className="border-r border-gray-300 px-2 py-2">
                      <input
                        type="text"
                        value={row.roomCategory}
                        onChange={(e) => {
                          const updated = [...extensionRows];
                          updated[rowIndex].roomCategory = e.target.value;
                          setExtensionRows(updated);
                        }}
                        disabled={!isEditingCover}
                        className="w-full px-2 py-1 rounded-md focus:outline-none text-sm bg-white border-2 border-blue-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-200 disabled:bg-white disabled:border-0 disabled:cursor-default disabled:px-0"
                      />
                    </div>
                    {/* Meals with Remarks */}
                    <div className="px-1 py-1 flex items-center justify-center">
                      <div className="flex flex-col gap-0.5 text-xs w-full">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={row.meals?.includes('Breakfast') ?? true}
                            onChange={(e) => {
                              const updated = [...extensionRows];
                              let meals = [...updated[rowIndex].meals];
                              if (e.target.checked) {
                                if (!meals.includes('Breakfast')) meals.push('Breakfast');
                              } else {
                                meals = meals.filter(m => m !== 'Breakfast');
                                updated[rowIndex].breakfastRemark = '';
                              }
                              updated[rowIndex].meals = meals;
                              setExtensionRows(updated);
                            }}
                            disabled={!isEditingCover}
                            className="w-3 h-3 cursor-pointer disabled:cursor-not-allowed"
                          />
                          <span>B</span>
                        </label>
                        {row.meals?.includes('Breakfast') && isEditingCover && (
                          <input
                            type="text"
                            placeholder="Breakfast at..."
                            value={row.breakfastRemark || ''}
                            onChange={(e) => {
                              const updated = [...extensionRows];
                              updated[rowIndex].breakfastRemark = e.target.value;
                              setExtensionRows(updated);
                            }}
                            className="w-full px-1 py-0.5 text-xs border rounded"
                          />
                        )}
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={row.meals?.includes('Lunch') ?? false}
                            onChange={(e) => {
                              const updated = [...extensionRows];
                              let meals = [...updated[rowIndex].meals];
                              if (e.target.checked) {
                                if (!meals.includes('Lunch')) meals.push('Lunch');
                              } else {
                                meals = meals.filter(m => m !== 'Lunch');
                                updated[rowIndex].lunchRemark = '';
                              }
                              updated[rowIndex].meals = meals;
                              setExtensionRows(updated);
                            }}
                            disabled={!isEditingCover}
                            className="w-3 h-3 cursor-pointer disabled:cursor-not-allowed"
                          />
                          <span>L</span>
                        </label>
                        {row.meals?.includes('Lunch') && isEditingCover && (
                          <input
                            type="text"
                            placeholder="Lunch at..."
                            value={row.lunchRemark || ''}
                            onChange={(e) => {
                              const updated = [...extensionRows];
                              updated[rowIndex].lunchRemark = e.target.value;
                              setExtensionRows(updated);
                            }}
                            className="w-full px-1 py-0.5 text-xs border rounded"
                          />
                        )}
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={row.meals?.includes('Dinner') ?? false}
                            onChange={(e) => {
                              const updated = [...extensionRows];
                              let meals = [...updated[rowIndex].meals];
                              if (e.target.checked) {
                                if (!meals.includes('Dinner')) meals.push('Dinner');
                              } else {
                                meals = meals.filter(m => m !== 'Dinner');
                                updated[rowIndex].dinnerRemark = '';
                              }
                              updated[rowIndex].meals = meals;
                              setExtensionRows(updated);
                            }}
                            disabled={!isEditingCover}
                            className="w-3 h-3 cursor-pointer disabled:cursor-not-allowed"
                          />
                          <span>D</span>
                        </label>
                        {row.meals?.includes('Dinner') && isEditingCover && (
                          <input
                            type="text"
                            placeholder="Dinner at..."
                            value={row.dinnerRemark || ''}
                            onChange={(e) => {
                              const updated = [...extensionRows];
                              updated[rowIndex].dinnerRemark = e.target.value;
                              setExtensionRows(updated);
                            }}
                            className="w-full px-1 py-0.5 text-xs border rounded"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowExtensionForm(false);
                    setExtensionPosition('post');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {isEditingCover ? 'Cancel' : 'Close'}
                </button>
                {isEditingCover && (
                  <button
                    onClick={() => {
                      // Validate passenger name
                      if (!extensionPassengerName.trim()) {
                        alert('Please enter passenger name');
                        return;
                      }
                      
                      // Validate that at least one row has data (date or sector)
                      const hasValidRow = extensionRows.some(row => row.date.trim() || row.sector.trim());
                      if (!hasValidRow) {
                        alert('Please enter at least one row with date or sector information');
                        return;
                      }
                      
                      // Filter out empty rows and assign passenger name to all rows
                      const validRows = extensionRows
                        .filter(row => row.date.trim() || row.sector.trim())
                        .map(row => ({
                          ...row,
                          passengerName: extensionPassengerName.trim(),
                          position: extensionPosition
                        }));
                      
                      if (currentExtension.id) {
                        // Update existing - remove old rows for the current passenger + placement and add updated rows
                        const targetPassenger = currentExtension.passengerName || extensionPassengerName.trim();
                        const targetPosition = currentExtension.position || extensionPosition;
                        const otherExtensions = extensions.filter(
                          e => !(e.passengerName === targetPassenger && (e.position || 'post') === targetPosition)
                        );
                        const updatedExtensions = validRows.map((row, idx) => ({
                          ...row,
                          id: idx === 0 && currentExtension.id ? currentExtension.id : Date.now().toString() + idx
                        }));
                        setExtensions([...otherExtensions, ...updatedExtensions]);
                      } else {
                        // Add new - add all valid rows with passenger name
                        const newExtensions = validRows.map((row, idx) => ({
                          ...row,
                          id: Date.now().toString() + idx
                        }));
                        setExtensions([...extensions, ...newExtensions]);
                      }
                      
                      setShowExtensionForm(false);
                      setExtensionPassengerName('');
                      setExtensionPosition('post');
                      setExtensionRows([{
                        id: '1',
                        passengerName: '',
                        position: 'post',
                        date: '',
                        sector: '',
                        flightNumber: '',
                        time: '',
                        status: '',
                        guideName: '',
                        guideType: '',
                        city: '',
                        hotelName: '',
                        hotelStatus: '',
                        roomCategory: '',
                        meals: ['Breakfast'],
                        breakfastRemark: '',
                        lunchRemark: '',
                        dinnerRemark: ''
                      }]);
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                  >
                    Save Extension
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Legacy cover preview (hidden, kept for reference) */}
      <div className="hidden">
        <div className="max-w-3xl mx-auto bg-gradient-to-br from-purple-50 to-blue-50 p-8 rounded-lg border-2 border-purple-200">
          <div className="space-y-6">
            {/* Header */}
            <div className="text-center border-b-2 border-purple-300 pb-4">
              <h3 className="text-2xl font-semibold text-purple-900">{tour.tourName}</h3>
              <p className="text-gray-600 mt-1">Tour File #{tour.id}</p>
            </div>

            {/* Client Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Client Name</p>
                <p className="font-semibold">{tour.clientName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Agent</p>
                <p className="font-semibold">{tour.ManagerName}</p>
              </div>
            </div>

            {/* Tour Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Total PAX</p>
                <p className="font-semibold">{tour.pax} Passengers</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">PNR</p>
                <p className="font-semibold">{tour.pnr}</p>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Start Date</p>
                <p className="font-semibold">{new Date(tour.startDate).toLocaleDateString('en-IN', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">End Date</p>
                <p className="font-semibold">{new Date(tour.endDate).toLocaleDateString('en-IN', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}</p>
              </div>
            </div>

            {/* Cities */}
            <div>
              <p className="text-sm text-gray-600 mb-2">Cities to Visit</p>
              <div className="flex flex-wrap gap-2">
                {Array.isArray(tour.cities) && tour.cities.length > 0 ? tour.cities.map(city => (
                  <Badge key={city} variant="secondary" className="bg-purple-100 text-purple-700">
                    {city}
                  </Badge>
                )) : <span className="text-gray-400 text-sm">No cities specified</span>}
              </div>
            </div>

            {/* Hotels */}
            <div>
              <p className="text-sm text-gray-600 mb-2">Hotels</p>
              <ul className="space-y-1">
                {Array.isArray(tour.hotels) && tour.hotels.length > 0 ? tour.hotels.map(hotel => (
                  <li key={hotel} className="text-sm">• {hotel}</li>
                )) : <li className="text-sm text-gray-400">No hotels specified</li>}
              </ul>
            </div>

            {/* Guide & Transport */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Guide</p>
                <p className="font-semibold">{tour.guide}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Transport</p>
                <p className="font-semibold">{tour.transportType}</p>
              </div>
            </div>

            {/* Revenue */}
            <div className="text-center pt-4 border-t-2 border-purple-300">
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-semibold text-purple-900">₹{tour.revenue.toLocaleString('en-IN')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs for different sections */}
      <Tabs defaultValue="conversations" className="space-y-6">
        <TabsList>
          <TabsTrigger value="conversations">
            <MessageSquare className="w-4 h-4 mr-2" />
            Conversations ({tour.conversations})
          </TabsTrigger>
          <TabsTrigger value="tickets">
            <Ticket className="w-4 h-4 mr-2" />
            Tickets ({tour.tickets})
          </TabsTrigger>
          <TabsTrigger value="hotels">
            <Hotel className="w-4 h-4 mr-2" />
            Hotels
          </TabsTrigger>
          <TabsTrigger value="passengers">
            <Users className="w-4 h-4 mr-2" />
            Passengers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="conversations">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold mb-4">Manager Notes & Remarks</h3>
            
            {/* Add New Note */}
            <div className="mb-6">
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 resize-none"
                rows={3}
                placeholder="Write a note or important remark about this tour file..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                disabled={savingNote || !tour || tour.id?.startsWith('new-')}
              />
              <div className="flex justify-end mt-2">
                <Button 
                  onClick={handleSaveNote} 
                  disabled={!newNote.trim() || savingNote || !tour || tour.id?.startsWith('new-')}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                >
                  {savingNote ? 'Saving...' : 'Save Note'}
                </Button>
              </div>
            </div>
            
            {/* Display Saved Notes */}
            <div className="space-y-3">
              {notes.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No notes yet. Add your first note above.</p>
              ) : (
                notes.map(note => (
                  <div key={note.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-700">{note.createdBy || 'Unknown User'}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(note.createdAt).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className="text-red-500 hover:text-red-700 text-sm"
                        title="Delete note"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.content}</p>
                  </div>
                ))
              )}
            </div>
            
            {/* Email Conversations (if any) */}
            {conversations.filter(c => c.tourId === tour.id).length > 0 && (
              <>
                <h3 className="font-semibold mb-4 mt-8">Email Conversation History</h3>
                <div className="space-y-3">
                  {conversations.filter(c => c.tourId === tour.id).map(conv => (
                    <div key={conv.id} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium">{conv.subject}</h4>
                        <span className="text-sm text-gray-500">{new Date(conv.date).toLocaleDateString()}</span>
                      </div>
                      <p className="text-sm text-gray-600">{conv.from}</p>
                      <p className="text-sm text-gray-500 mt-1">{conv.preview}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="tickets">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold mb-4">Tickets & Bookings</h3>
            <div className="space-y-3">
              {tickets.filter(t => t.tourId === tour.id).map(ticket => (
                <div key={ticket.id} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline">{ticket.type}</Badge>
                    {ticket.pnr && <span className="text-sm text-gray-600">PNR: {ticket.pnr}</span>}
                  </div>
                  {ticket.type === 'Flight' && (
                    <p className="text-sm">{ticket.from} → {ticket.to} on {new Date(ticket.date!).toLocaleDateString()}</p>
                  )}
                  {ticket.type === 'Hotel' && (
                    <p className="text-sm">{ticket.name} - Check in: {new Date(ticket.checkIn!).toLocaleDateString()}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="hotels">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Hotel Bookings</h3>
              <p className="text-xs text-gray-500">Generate a separate mail for each hotel</p>
            </div>
            <div className="space-y-3">
              {getDisplayHotels().length > 0 ? getDisplayHotels().map((hotel, idx) => (
                <div key={idx} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Hotel className="w-5 h-5 text-purple-600" />
                      <div>
                        <h4 className="font-medium">{hotel}</h4>
                        <p className="text-sm text-gray-600">Room Nights: {Math.max(1, Math.round((tour.roomNights || 0) / Math.max(1, getDisplayHotels().length)))}</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-violet-300 text-violet-700 hover:bg-violet-50"
                      onClick={() => openHotelBookingMail(hotel)}
                    >
                      Booking
                    </Button>
                  </div>
                </div>
              )) : <p className="text-gray-400 text-sm">No hotel bookings yet</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="passengers">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold mb-4">Passenger Details</h3>
            <div className="space-y-3">
              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Total Passengers</p>
                    <p className="text-2xl font-semibold text-purple-600 mt-1">{tour.pax}</p>
                  </div>
                  <Users className="w-12 h-12 text-purple-600 opacity-20" />
                </div>
              </div>
              
              {/* Editable Passenger Forms (when isEditingCover is true) */}
              {isEditingCover ? (
                <div className="space-y-4">
                  <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ImageUp className="w-4 h-4 text-blue-700" />
                      <p className="text-sm font-medium text-blue-900">Upload Passenger Screenshot(s)</p>
                    </div>
                    <p className="text-xs text-blue-800 mb-3">
                      Upload passport or ID screenshots. The files will be attached to the mail draft for review.
                    </p>
                    <div className="flex items-center gap-3">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          handlePassengerAttachmentUpload(e.target.files);
                          e.currentTarget.value = '';
                        }}
                        multiple
                        className="text-sm"
                      />
                    </div>
                    {passengerAttachments.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {passengerAttachments.map((attachment, index) => (
                          <span key={`${attachment.filename}-${index}`} className="inline-flex items-center gap-2 rounded-full bg-blue-100 text-blue-800 px-3 py-1 text-xs">
                            {attachment.filename}
                            <button
                              type="button"
                              onClick={() => removePassengerAttachment(index)}
                              className="hover:text-blue-950"
                              aria-label={`Remove ${attachment.filename}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {passengerDetails.map((passenger, idx) => (
                    <div key={passenger.id} className="border-2 border-gray-300 rounded-lg p-4">
                      <h4 className="text-lg font-semibold mb-3 text-purple-600">Passenger {idx + 1}</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-black mb-1">Room Type</label>
                          <select
                            className="w-full px-3 py-2 border-2 border-purple-400 rounded-md focus:outline-none focus:border-purple-600"
                            value={passenger.roomType}
                            onChange={(e) => {
                              const updated = [...passengerDetails];
                              updated[idx].roomType = e.target.value;  
                              // Clear room number if not Twin
                              if (e.target.value !== 'Twin') {
                                updated[idx].roomNumber = '';
                              }
                              setPassengerDetails(updated);
                            }}
                          >
                            <option value="">Select...</option>
                            <option value="Single">Single</option>
                            <option value="Double">Double</option>
                            <option value="Twin">Twin</option>
                            <option value="Triple">Triple</option>
                          </select>
                        </div>
                        
                        {/* Room Number - only for Twin rooms */}
                        {passenger.roomType === 'Twin' && (
                          <div>
                            <label className="block text-sm font-medium text-black mb-1">Room Number</label>
                            <input
                              type="text"
                              className="w-full px-3 py-2 border-2 border-purple-400 rounded-md focus:outline-none focus:border-purple-600"
                              value={passenger.roomNumber || ''}
                              onChange={(e) => {
                                const updated = [...passengerDetails];
                                updated[idx].roomNumber = e.target.value;
                                setPassengerDetails(updated);
                              }}
                              placeholder="e.g., 5"
                            />
                          </div>
                        )}
                        
                        <div>
                          <label className="block text-sm font-medium text-black mb-1">Passenger Name *</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border-2 border-purple-400 rounded-md focus:outline-none focus:border-purple-600"
                            value={passenger.name}
                            onChange={(e) => {
                              const updated = [...passengerDetails];
                              updated[idx].name = e.target.value;
                              setPassengerDetails(updated);
                            }}
                            placeholder="Full name"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-black mb-1">Date of Birth</label>
                          <input
                            type="date"
                            className="w-full px-3 py-2 border-2 border-purple-400 rounded-md focus:outline-none focus:border-purple-600"
                            value={passenger.dob}
                            onChange={(e) => {
                              const updated = [...passengerDetails];
                              updated[idx].dob = e.target.value;
                              // Auto-calculate age
                              if (e.target.value) {
                                const today = new Date();
                                const birthDate = new Date(e.target.value);
                                const age = today.getFullYear() - birthDate.getFullYear();
                                updated[idx].age = age.toString();
                              }
                              setPassengerDetails(updated);
                            }}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-black mb-1">Age</label>
                          <input
                            type="number"
                            className="w-full px-3 py-2 border-2 border-purple-400 rounded-md focus:outline-none focus:border-purple-600"
                            value={passenger.age}
                            onChange={(e) => {
                              const updated = [...passengerDetails];
                              updated[idx].age = e.target.value;
                              setPassengerDetails(updated);
                            }}
                            placeholder="Age"
                            min="0"
                            max="120"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-black mb-1">Sex</label>
                          <select
                            className="w-full px-3 py-2 border-2 border-purple-400 rounded-md focus:outline-none focus:border-purple-600"
                            value={passenger.sex}
                            onChange={(e) => {
                              const updated = [...passengerDetails];
                              updated[idx].sex = e.target.value;
                              setPassengerDetails(updated);
                            }}
                          >
                            <option value="">Select...</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-black mb-1">Nationality</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border-2 border-purple-400 rounded-md focus:outline-none focus:border-purple-600"
                            value={passenger.nationality}
                            onChange={(e) => {
                              const updated = [...passengerDetails];
                              updated[idx].nationality = e.target.value;
                              setPassengerDetails(updated);
                            }}
                            placeholder="Nationality"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-black mb-1">Passport No.</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border-2 border-purple-400 rounded-md focus:outline-none focus:border-purple-600"
                            value={passenger.passportNo}
                            onChange={(e) => {
                              const updated = [...passengerDetails];
                              updated[idx].passportNo = e.target.value;
                              setPassengerDetails(updated);
                            }}
                            placeholder="Passport number"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-black mb-1">Date of Issue</label>
                          <input
                            type="date"
                            className="w-full px-3 py-2 border-2 border-purple-400 rounded-md focus:outline-none focus:border-purple-600"
                            value={passenger.issueDate}
                            onChange={(e) => {
                              const updated = [...passengerDetails];
                              updated[idx].issueDate = e.target.value;
                              setPassengerDetails(updated);
                            }}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-black mb-1">Expiry Date</label>
                          <input
                            type="date"
                            className="w-full px-3 py-2 border-2 border-purple-400 rounded-md focus:outline-none focus:border-purple-600"
                            value={passenger.expiryDate}
                            onChange={(e) => {
                              const updated = [...passengerDetails];
                              updated[idx].expiryDate = e.target.value;
                              setPassengerDetails(updated);
                            }}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-black mb-1">Place of Issue</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border-2 border-purple-400 rounded-md focus:outline-none focus:border-purple-600"
                            value={passenger.placeOfIssue}
                            onChange={(e) => {
                              const updated = [...passengerDetails];
                              updated[idx].placeOfIssue = e.target.value;
                              setPassengerDetails(updated);
                            }}
                            placeholder="Place of issue"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-black mb-1">Dietary Requirements</label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border-2 border-purple-400 rounded-md focus:outline-none focus:border-purple-600"
                            value={passenger.dietaryRequirements || ''}
                            onChange={(e) => {
                              const updated = [...passengerDetails];
                              updated[idx].dietaryRequirements = e.target.value;
                              setPassengerDetails(updated);
                            }}
                            placeholder="e.g., Vegetarian, Vegan, Gluten-free"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <p className="text-sm text-gray-500 italic mt-2">
                    💡 Changes will be saved when you click "Save Changes" in the Cover tab
                  </p>
                </div>
              ) : (
                /* Read-only Naming/Rooming List Table */
                (() => {
                  const passengers = passengerDetails.filter((p: any) => p.name);
                  
                  if (passengers.length === 0) {
                    return (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No passenger details added yet. Click "Edit Cover" to add passenger information.
                      </p>
                    );
                  }
                  
                  return (
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-blue-600">
                          <tr>
                            <th className="px-3 py-2 text-center font-semibold text-black">Room</th>
                            <th className="px-3 py-2 text-center font-semibold text-black">Room Type</th>
                            <th className="px-3 py-2 text-left font-semibold text-black">Passenger</th>
                            <th className="px-3 py-2 text-center font-semibold text-black">DOB</th>
                            <th className="px-3 py-2 text-center font-semibold text-black">Age</th>
                            <th className="px-3 py-2 text-center font-semibold text-black">Sex</th>
                            <th className="px-3 py-2 text-center font-semibold text-black">Nationality</th>
                            <th className="px-3 py-2 text-center font-semibold text-black">Passport No.</th>
                            <th className="px-3 py-2 text-center font-semibold text-black">Date Of Issue</th>
                            <th className="px-3 py-2 text-center font-semibold text-black">Expiry Date</th>
                            <th className="px-3 py-2 text-center font-semibold text-black">Place Of Issue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {passengers.map((p: any, idx: number) => (
                            <tr key={idx} className="border-t border-gray-200 hover:bg-gray-50">
                              <td className="px-3 py-2 text-center font-medium">{idx + 1}</td>
                              <td className="px-3 py-2 text-center">{p.roomType || ''}</td>
                              <td className="px-3 py-2 font-medium">{p.name}</td>
                              <td className="px-3 py-2 text-center">{p.dob ? new Date(p.dob).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</td>
                              <td className="px-3 py-2 text-center">{p.age || ''}</td>
                              <td className="px-3 py-2 text-center">{p.sex || ''}</td>
                              <td className="px-3 py-2 text-center">{p.nationality || ''}</td>
                              <td className="px-3 py-2 text-center">{p.passportNo || ''}</td>
                              <td className="px-3 py-2 text-center">{p.issueDate ? new Date(p.issueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</td>
                              <td className="px-3 py-2 text-center">{p.expiryDate ? new Date(p.expiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</td>
                              <td className="px-3 py-2 text-center">{p.placeOfIssue || ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {showBookingMailModal && (
        <SendToHotelModal
          isOpen={showBookingMailModal}
          onClose={() => {
            setShowBookingMailModal(false);
            setSelectedHotelForMail('');
            setSelectedHotelBookings([]);
          }}
          hotelName={selectedHotelForMail}
          bookings={selectedHotelBookings}
          mode="hotels"
          tourFileData={tour}
          selectedHotelName={selectedHotelForMail}
          attachments={passengerAttachments}
        />
      )}
    </div>
  );
}
